import React, { useCallback, useEffect, useRef, useState } from 'react'
import AuthSplitCard from './auth/AuthSplitCard'
import { Box, CircularProgress, Typography, Button, Alert } from '@mui/material'
import Parse from '../services/initializeParse'
import authService from '../services/authService'
import { useAppDispatch } from '../hooks'
import { setAuthenticated, setUserData, setPlayerName } from '../stores/UserStore'
import store from '../stores'
import type { UserData, UserRole } from '../types/user'
import { useRoutingStore } from '../stores/routingStore'
import { isPlaceholderInvitationQuery } from '../constants/invitationEmailPlaceholder'
import { readParseGoogleLinked, readParseNeedsPassword } from '../account'
import AccountPasswordPanel from './profile/AccountPasswordPanel'
import MarketingEmailCheckbox from './consent/MarketingEmailCheckbox'
import TermsNotice from './consent/TermsNotice'
import { buildSignupConsent } from '../consent/consentService'
import { TERMS_NOTICE_ACTION } from '../consent/consentCopy'
import { useLegalLinks } from '../consent/useLegalLinks'
import CompanyDisabledScreen from './common/CompanyDisabledScreen'
import {
  COMPANY_BLOCKED_MESSAGES,
  fetchCompanyAccess,
  isCompanyBlockedCloudError,
  type CompanyAccess,
} from '../services/CompanyAccessService'
import './AuthenticationDialog.scss'
import { getDisplayName } from '../utils/personName'

interface AcceptInvitationPageProps {
  onComplete: () => void
}

type Phase = 'redeeming' | 'password' | 'finishing' | 'error' | 'companyBlocked'

type RedeemResult = {
  sessionToken: string
  requiresPasswordSetup?: boolean
  /** The caller was already a member and re-opened a consumed link (cloud SC-313 path). */
  alreadyRedeemed?: boolean
}

type InvitationEntry = {
  authResponse: Awaited<ReturnType<typeof authService.establishSessionFromInvitationMagicLink>>
  /** Set when the invitation's company is suspended or deleted (SC-409). */
  blocked: CompanyAccess | null
}

/**
 * Single-flight guard for the redemption call.
 *
 * The effect's `cancelled` flag only suppresses setState — it does not stop a
 * second Parse.Cloud.run. React StrictMode double-invokes effects in dev, and any
 * remount would fire redemption again. Module scope (rather than a ref) so the
 * guard survives the unmount/remount cycle StrictMode performs.
 */
let redemptionInFlight: Promise<RedeemResult> | null = null

/**
 * Same single-flight for what follows redemption: becoming the user and checking the
 * company. Without it a remount could become the user again after the blocked path had
 * already signed that session out, leaving a dead session behind.
 */
let entryInFlight: Promise<InvitationEntry> | null = null

interface PasswordStepPanelProps {
  marketingEmailOptIn: boolean
  onMarketingEmailOptInChange: (checked: boolean) => void
  onSaved: () => void
}

/**
 * Mounted with the password step only, so the policy lookup runs once the invitee's session
 * exists rather than racing the redemption call. One lookup feeds both the notice and the
 * consent sent with the password, so the Terms version recorded is the one shown. [SC-360]
 */
function PasswordStepPanel({
  marketingEmailOptIn,
  onMarketingEmailOptInChange,
  onSaved,
}: PasswordStepPanelProps) {
  const legal = useLegalLinks()
  return (
    <AccountPasswordPanel
      googleLinked={readParseGoogleLinked()}
      hasManualPassword={false}
      onSaved={onSaved}
      title="Create your password"
      description="You'll need this to sign in next time, so pick something you'll remember."
      consent={buildSignupConsent(marketingEmailOptIn, legal.termsVersion)}
      beforeSubmit={
        <>
          <MarketingEmailCheckbox
            id="invite-marketing-email"
            checked={marketingEmailOptIn}
            onChange={onMarketingEmailOptInChange}
            sx={{ mt: 1 }}
          />
          <TermsNotice
            action={TERMS_NOTICE_ACTION.setPassword}
            links={legal}
            sx={{
              mt: 1,
              fontSize: '0.8125rem',
              lineHeight: 1.5,
              color: 'var(--sevv-text-secondary, #757575)',
              '& a': { color: 'var(--sevv-primary, #f97415)', fontWeight: 500 },
            }}
          />
        </>
      }
    />
  )
}

/** Exposed for tests; production code never needs to reset this. */
export function __resetRedemptionGuardForTests() {
  redemptionInFlight = null
  entryInFlight = null
}

/**
 * A suspended or deleted company's invitation must not let anyone in (SC-409). A cloud that
 * checks the company refuses redemption itself (handled in the page's catch); this check runs
 * after every successful redemption as the safety net for a cloud without that redeem-time block,
 * which stays live until the manual Back4App upload. It runs right after the session exists and
 * before the password step and `completeCompanyInvitation`. For an invitee who still owes a
 * password that leaves the token unconsumed, so the link works again after reactivation. An
 * invitee who already has a password or a linked Google account loses the token regardless: an
 * older cloud's redemption clears it before this check runs, so after reactivation that link
 * answers "invalid or expired". Their membership is untouched by the suspension, so they sign in
 * normally with that password or Google account instead. The minted session is signed out so
 * none is left behind.
 *
 * `/api/company/me/access` answers for the profile's company (or the oldest active membership).
 * Redeeming an invitation normally points the profile at the invited company, but a user with
 * several memberships can get an answer about another one, which says nothing about this
 * invitation. Only a block on the invited company counts; otherwise the user goes through, failing
 * open like a failed check, and the backend still refuses room joins, the lobby and chat.
 *
 * An existing member who re-opened a consumed link keeps today's flow; the workspace gate
 * handles them. If the check itself fails, today's flow continues and the backend still
 * refuses room joins, the lobby and chat.
 */
async function enterInvitation(
  sessionToken: string,
  alreadyRedeemed: boolean,
  invitedCompanyId: string
): Promise<InvitationEntry> {
  const authResponse = await authService.establishSessionFromInvitationMagicLink(sessionToken)
  if (alreadyRedeemed) return { authResponse, blocked: null }

  let access: CompanyAccess | null = null
  try {
    access = await fetchCompanyAccess({ sessionToken })
  } catch (e) {
    console.warn('[AcceptInvitation] Company access check failed; continuing:', e)
  }
  if (!access?.blocked || access.companyId !== invitedCompanyId) {
    return { authResponse, blocked: null }
  }

  try {
    await authService.signOut()
  } catch (e) {
    console.warn('[AcceptInvitation] Sign-out after a blocked invitation failed:', e)
  }
  return { authResponse, blocked: access }
}

/**
 * Full-page flow for /accept-invitation?token=...&company=...
 *
 * Redeems the Cloud token, becomes the Parse user, then — if the account has no
 * password of its own — requires one before letting them through to the dashboard.
 * For an invitee who owes a password, the token is consumed only once that password is set, so
 * abandoning this page leaves the emailed link still usable. For one who already has a password
 * or a linked Google account, redemption consumes the token immediately, so the link is spent
 * as soon as this page loads.
 */
export default function AcceptInvitationPage({ onComplete }: AcceptInvitationPageProps) {
  const dispatch = useAppDispatch()
  const [error, setError] = useState<string | null>(null)
  const [phase, setPhase] = useState<Phase>('redeeming')
  const [blockedAccess, setBlockedAccess] = useState<CompanyAccess | null>(null)
  // Unticked by default: marketing email needs an active opt-in (SC-360).
  const [marketingEmailOptIn, setMarketingEmailOptIn] = useState(false)
  const invitationRef = useRef<{ token: string; company: string } | null>(null)

  /**
   * Clears the single-use invitation token. Deliberately non-fatal: the cloud
   * function may not be deployed yet (cloud ships separately and manually), and a
   * token left behind simply expires on its own TTL — strictly better than
   * stranding the user, which is what consuming it too early used to do.
   */
  const completeInvitation = useCallback(async () => {
    const invitation = invitationRef.current
    if (!invitation) return
    try {
      await Parse.Cloud.run('completeCompanyInvitation', {
        token: invitation.token,
        companyId: invitation.company,
      })
    } catch (e) {
      console.warn('[AcceptInvitation] completeCompanyInvitation failed (non-fatal):', e)
    }
  }, [])

  const enterDashboard = useCallback(() => {
    window.history.replaceState({}, document.title, '/dashboard')
    useRoutingStore.getState().syncWithURL()
    onComplete()
  }, [onComplete])

  const handlePasswordSaved = useCallback(async () => {
    setPhase('finishing')
    await completeInvitation()
    enterDashboard()
  }, [completeInvitation, enterDashboard])

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      const params = new URLSearchParams(window.location.search)
      const token = params.get('token')
      const company = params.get('company')
      if (!token || !company) {
        if (!cancelled) {
          setError('Invalid invitation link. Please use the link from your email.')
          setPhase('error')
        }
        return
      }

      if (isPlaceholderInvitationQuery(token, company)) {
        if (!cancelled) {
          setError(
            'This link is only for email template preview or test sends. It cannot sign you in. To try a real invitation, use Company Management to invite a user.',
          )
          setPhase('error')
        }
        return
      }

      invitationRef.current = { token, company }

      try {
        if (!redemptionInFlight) {
          redemptionInFlight = Parse.Cloud.run('redeemCompanyInvitation', {
            token,
            companyId: company,
          }) as Promise<RedeemResult>
        }
        const { sessionToken, requiresPasswordSetup, alreadyRedeemed } = await redemptionInFlight

        if (!entryInFlight) {
          entryInFlight = enterInvitation(sessionToken, alreadyRedeemed === true, company)
        }
        const { authResponse, blocked } = await entryInFlight

        if (blocked) {
          // Nothing below runs: no user in the store, no password step, token not consumed.
          if (!cancelled) {
            setBlockedAccess(blocked)
            setPhase('companyBlocked')
          }
          return
        }

        const current = Parse.User.current()
        const emailVerified = current?.get('emailVerified') === true

        const userData: UserData = {
          id: authResponse.user.id,
          email: authResponse.user.email,
          username: authResponse.user.username,
          role: (authResponse.profile?.role as UserRole) || 'user',
          createdAt: authResponse.user.createdAt
            ? typeof authResponse.user.createdAt === 'string'
              ? authResponse.user.createdAt
              : new Date(authResponse.user.createdAt).toISOString()
            : new Date().toISOString(),
          firstName: authResponse.profile?.firstName || '',
          lastName: authResponse.profile?.lastName || '',
          displayName: authResponse.profile?.displayName || '',
          title: authResponse.profile?.title || '',
          contact: authResponse.profile?.contact || '',
          bio: authResponse.profile?.bio || '',
          avatar: authResponse.profile?.avatar || 'adam',
          gameCharacter: authResponse.profile?.gameCharacter || 'adam',
          customAvatarUrl: authResponse.profile?.customAvatarUrl || '',
          profileCompleted: authResponse.profile?.profileCompleted || false,
          emailVerified,
        }

        dispatch(setUserData(userData))
        const sessionId = store.getState().user.sessionId
        if (sessionId) {
          const displayName = getDisplayName(userData)
          dispatch(setPlayerName({ id: sessionId, name: displayName }))
        }
        dispatch(setAuthenticated(true))

        if (cancelled) return

        // Derive the gate client-side rather than trusting the server flag alone.
        // Cloud code deploys separately and manually, so `requiresPasswordSetup`
        // is undefined against an older backend — reading the user's own
        // hasManualPassword means the step still appears correctly either way.
        const needsPassword = readParseNeedsPassword() || requiresPasswordSetup === true

        if (needsPassword) {
          // Stop here. The token stays unconsumed until the password is saved, so
          // closing the tab now leaves the emailed link working.
          setPhase('password')
          return
        }

        setPhase('finishing')
        await completeInvitation()
        if (!cancelled) enterDashboard()
      } catch (e: unknown) {
        // A failed redemption must not leave a resolved promise cached, or a retry
        // in this tab would reuse the failure.
        redemptionInFlight = null
        entryInFlight = null
        const anyErr = e as { message?: string; code?: number }

        // A cloud that checks the company refuses redemption itself (SC-409): no session is
        // minted and the token is untouched, so the same link works after reactivation. With
        // no session there is no company name to show; the screen words it generically.
        if (isCompanyBlockedCloudError(e)) {
          if (!cancelled) {
            setBlockedAccess({
              companyId: null,
              companyName: null,
              role: null,
              blocked: true,
              reason: anyErr.message === COMPANY_BLOCKED_MESSAGES.deleted ? 'deleted' : 'suspended',
            })
            setPhase('companyBlocked')
          }
          return
        }

        let msg =
          anyErr?.message ||
          'We could not accept this invitation. The link may have expired; ask your administrator to resend it.'
        if (
          typeof anyErr?.code === 'number' &&
          anyErr.code === Parse.Error.OPERATION_FORBIDDEN &&
          msg.toLowerCase().includes('invalid') &&
          msg.toLowerCase().includes('expired')
        ) {
          msg = `${msg} Each link works only once—if you already opened it, ask your administrator to resend the invitation.`
        }
        if (!cancelled) {
          setError(msg)
          setPhase('error')
        }
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [dispatch, completeInvitation, enterDashboard])

  const goHome = () => {
    window.history.replaceState({}, document.title, '/')
    window.location.reload()
  }

  const isBusy = phase === 'redeeming' || phase === 'finishing'

  return (
    // SC-438: every state of the accept page sits in the sign-in dialog's split layout.
    <AuthSplitCard screen="invite">
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
      {isBusy && (
        <>
          <CircularProgress sx={{ color: 'var(--sevv-primary, #f97415)', mb: 2 }} />
          <Typography variant="body1" sx={{ color: 'var(--sevv-text-secondary, #757575)' }}>
            {phase === 'finishing' ? 'Setting up your account…' : 'Signing you in…'}
          </Typography>
        </>
      )}

      {/* Mandatory step — rendered as the whole page, so there is no close
          affordance to dismiss and no route past it without saving a password. */}
      {phase === 'password' && (
        // The light surface and its text colours come from AuthSplitCard. [SC-313]
        <Box sx={{ width: '100%' }}>
          <Typography
            variant="h5"
            component="h1"
            sx={{ fontWeight: 700, mb: 0.5, color: 'var(--sevv-text-primary, #424242)' }}
          >
            Create your password
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'var(--sevv-text-secondary, #757575)' }}>
            Set a password to finish — you&apos;ll use it to sign in from now on.
          </Typography>
          <PasswordStepPanel
            marketingEmailOptIn={marketingEmailOptIn}
            onMarketingEmailOptInChange={setMarketingEmailOptIn}
            onSaved={() => void handlePasswordSaved()}
          />
        </Box>
      )}

      {phase === 'companyBlocked' && blockedAccess && (
        <Box
          sx={{
            width: '100%',
            // Light surface over a theme built for the dark canvas: state the text colours,
            // as the password step above does. [SC-313]
            '& .CompanyDisabledScreen-title': { color: 'var(--sevv-text-primary, #424242)' },
            '& .CompanyDisabledScreen-text': { color: 'var(--sevv-text-secondary, #757575)' },
          }}
        >
          <CompanyDisabledScreen
            variant="invitation"
            companyName={blockedAccess.companyName}
            reason={blockedAccess.reason}
          >
            <Button
              variant="contained"
              onClick={goHome}
              sx={{ bgcolor: 'var(--sevv-primary, #f97415)' }}
            >
              Go to sign in
            </Button>
          </CompanyDisabledScreen>
        </Box>
      )}

      {phase === 'error' && error && (
        <Box sx={{ maxWidth: 440, width: '100%' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            onClick={goHome}
            fullWidth
            sx={{ bgcolor: 'var(--sevv-primary, #f97415)' }}
          >
            Go to sign in
          </Button>
        </Box>
      )}
      </Box>
    </AuthSplitCard>
  )
}
